import { useCallback, useEffect, useRef, useState } from "react";

import { migrateCardsToGrid } from "components/Report-generator/utils/gridUtils";
import { useReportsActions } from "store";
import type {
  CanvasCard,
  Currency,
  DateRangeValue,
  Filters,
  ReportMeta,
  ReportOrientation,
  ReportPage
} from "store/types";

import { buildContentSummary, stripCardData } from "./useViewTemplate";

const DEBOUNCE_MS = 500;

interface UseLastViewProps {
  filters: Filters;
  cards: CanvasCard[];
  period: DateRangeValue | null;
  currency: Currency;
  reportMeta: ReportMeta;
  pages: ReportPage[];
  orientation: ReportOrientation;
  logoFile: File | null;
  backgroundFile: File | null;
  setCards: (cards: CanvasCard[]) => void;
  setFilters: (filters: Filters) => void;
  setLogoFile: (file: File | null) => void;
  setBackgroundFile: (file: File | null) => void;
  setPeriod: (period: DateRangeValue | null) => void;
  setCurrency: (currency: Currency) => void;
  setReportMeta: React.Dispatch<React.SetStateAction<ReportMeta>>;
  setPages: (pages: ReportPage[]) => void;
  setOrientation: (orientation: ReportOrientation) => void;
}

interface UseLastViewReturn {
  lastViewLoaded: boolean;
  deleteLastView: () => Promise<void>;
}

export const useLastView = ({
  filters,
  cards,
  period,
  currency,
  reportMeta,
  pages,
  orientation,
  logoFile,
  backgroundFile,
  setCards,
  setFilters,
  setLogoFile,
  setBackgroundFile,
  setPeriod,
  setCurrency,
  setReportMeta,
  setPages,
  setOrientation
}: UseLastViewProps): UseLastViewReturn => {
  const { getLastViewAction, saveLastViewAction, updateReportTemplateAction, deleteLastViewAction } =
    useReportsActions();

  const [lastViewId, setLastViewId] = useState<number | null>(null);
  const [lastViewLoaded, setLastViewLoaded] = useState(false);
  const savingRef = useRef(false);

  // ── Auto-restore on mount ─────────────────────────────────────────
  useEffect(() => {
    let cancelled = false;

    const restore = async () => {
      try {
        const template = await getLastViewAction();

        if (cancelled) return;

        if (!template) {
          setLastViewLoaded(true);
          return;
        }

        setLastViewId(template.id);

        let parsedContent;
        try {
          parsedContent = typeof template.content === "string" ? JSON.parse(template.content) : template.content;
        } catch {
          setLastViewLoaded(true);
          return;
        }

        if (parsedContent) {
          if (parsedContent.filters) setFilters(parsedContent.filters);
          if (parsedContent.cards) setCards(migrateCardsToGrid(parsedContent.cards));
          if (parsedContent.period) setPeriod(parsedContent.period);
          if (parsedContent.currency) setCurrency(parsedContent.currency);
          if (parsedContent.reportMeta) {
            setReportMeta({ ...parsedContent.reportMeta, logo: "", background: "" });
          }
          if (parsedContent.pages) setPages(parsedContent.pages);
          if (parsedContent.orientation) setOrientation(parsedContent.orientation);
        }

        if (template.report_title) {
          setReportMeta((prev) => ({ ...prev, title: template.report_title || "" }));
        }

        const logoFileFromServer = template.files?.find((f) => f.type === "logo");
        if (logoFileFromServer?.file_url) {
          setReportMeta((prev) => ({ ...prev, logo: logoFileFromServer.file_url }));
        }

        const backgroundFileFromServer = template.files?.find((f) => f.type === "background");
        if (backgroundFileFromServer?.file_url) {
          setReportMeta((prev) => ({ ...prev, background: backgroundFileFromServer.file_url }));
        }

        setLogoFile(null);
        setBackgroundFile(null);
      } catch {
        // Silently fail — user just gets a blank canvas
      } finally {
        if (!cancelled) setLastViewLoaded(true);
      }
    };

    void restore();

    return () => {
      cancelled = true;
    };
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // ── Auto-save (debounced) ─────────────────────────────────────────
  // Keep refs for values needed inside the debounce callback so the effect
  // only re-runs when the fingerprint (structural content) actually changes.
  // Without this, reference-only changes (e.g. card data loading) would
  // cancel the pending timer via the cleanup, then the fingerprint guard
  // would short-circuit, silently dropping the save.
  const cardsRef = useRef(cards);
  cardsRef.current = cards;
  const filtersRef = useRef(filters);
  filtersRef.current = filters;
  const periodRef = useRef(period);
  periodRef.current = period;
  const currencyRef = useRef(currency);
  currencyRef.current = currency;
  const reportMetaRef = useRef(reportMeta);
  reportMetaRef.current = reportMeta;
  const pagesRef = useRef(pages);
  pagesRef.current = pages;
  const orientationRef = useRef(orientation);
  orientationRef.current = orientation;
  const logoFileRef = useRef(logoFile);
  logoFileRef.current = logoFile;
  const backgroundFileRef = useRef(backgroundFile);
  backgroundFileRef.current = backgroundFile;
  const lastViewIdRef = useRef(lastViewId);
  lastViewIdRef.current = lastViewId;

  const fingerprint = JSON.stringify({
    cards: stripCardData(cards),
    filters,
    period,
    currency,
    reportMeta: { ...reportMeta, logo: "", background: "" },
    pages,
    orientation
  });

  const prevFingerprintRef = useRef<string | null>(null);

  // Capture the baseline fingerprint once restore completes so the auto-save
  // effect doesn't re-save the just-restored state, but also doesn't swallow
  // the very first user change on a fresh/empty canvas.
  useEffect(() => {
    if (lastViewLoaded && prevFingerprintRef.current === null) {
      prevFingerprintRef.current = fingerprint;
    }
  }, [lastViewLoaded, fingerprint]);

  useEffect(() => {
    if (!lastViewLoaded) return;

    // No actual change (or baseline not yet captured)
    if (prevFingerprintRef.current === null || prevFingerprintRef.current === fingerprint) return;
    prevFingerprintRef.current = fingerprint;

    // Last card was removed → delete the last view instead of saving an empty canvas
    if (cardsRef.current.length === 0) {
      if (lastViewIdRef.current) {
        deleteLastViewAction().catch(() => {});
        setLastViewId(null);
      }
      return;
    }

    const timer = setTimeout(() => {
      if (savingRef.current) return;
      savingRef.current = true;

      const curCards = cardsRef.current;
      const curFilters = filtersRef.current;
      const curPeriod = periodRef.current;
      const curCurrency = currencyRef.current;
      const curReportMeta = reportMetaRef.current;
      const curPages = pagesRef.current;
      const curOrientation = orientationRef.current;
      const curLogoFile = logoFileRef.current;
      const curBackgroundFile = backgroundFileRef.current;
      const curLastViewId = lastViewIdRef.current;

      const contentPayload = {
        filters: curFilters,
        cards: stripCardData(curCards),
        period: curPeriod,
        currency: curCurrency,
        reportMeta: { ...curReportMeta, logo: "", background: "" },
        pages: curPages,
        orientation: curOrientation
      };

      const payload: Record<string, unknown> = {
        name: "Last View",
        content: JSON.stringify(contentPayload),
        content_summary: JSON.stringify(
          buildContentSummary(curCards, curOrientation, curCurrency, curPeriod, curFilters)
        ),
        report_title: curReportMeta.title,
        template_type: "last_view"
      };

      if (curLogoFile) {
        payload.logo = curLogoFile;
      } else if (curReportMeta.logo) {
        payload.logo_url = curReportMeta.logo;
      }

      if (curBackgroundFile) {
        payload.background = curBackgroundFile;
      } else if (curReportMeta.background) {
        payload.background_url = curReportMeta.background;
      }

      const save = curLastViewId
        ? updateReportTemplateAction(
            curLastViewId,
            payload as unknown as Parameters<typeof updateReportTemplateAction>[1]
          )
        : saveLastViewAction(payload as unknown as Parameters<typeof saveLastViewAction>[0]);

      save
        .then((result) => {
          if (result?.id) setLastViewId(result.id);
        })
        .catch(() => {
          // Silently fail — auto-save is best-effort
        })
        .finally(() => {
          savingRef.current = false;
        });
    }, DEBOUNCE_MS);

    return () => clearTimeout(timer);
  }, [fingerprint, lastViewLoaded, saveLastViewAction, updateReportTemplateAction, deleteLastViewAction]);

  // ── Delete ────────────────────────────────────────────────────────
  const deleteLastView = useCallback(async () => {
    if (lastViewId) {
      try {
        await deleteLastViewAction();
      } catch {
        // Silently fail
      }
    }
    setLastViewId(null);
  }, [lastViewId, deleteLastViewAction]);

  return { lastViewLoaded, deleteLastView };
};
